import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress, makeStyles } from "@material-ui/core";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ModalLayout from "@View/components/ModalLayout";
import ButtonBB from "@View/components/buttons/ButtonBB";
import { getRoleSafe } from "@Security/stateHelpers";
import { getPointColorFromString } from "@Utils/colorPicker";
import { getStopFactorReport, recalculateStopFactors } from "@Dao/stopFactorsApi";
import { StopFactorOwner, StopFactorReport, StopFactorResult, StopFactorStatus } from "@Constants/types";

const MUTED = "#A1ADBA";
const BORDER = "#E1E6F1";
const TEXT = "#1A1E24";
const RED = "#A3241C";
const GREEN = "#3DA66B";

const useStyles = makeStyles(() => ({
  summary: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: "8px 20px",
    padding: "4px 0 12px",
    borderBottom: `1px solid ${BORDER}`,
    fontSize: "13px",
    color: "#54606F",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "24px minmax(0, 2fr) minmax(0, 1.4fr) 90px",
    gap: "20px",
    alignItems: "center",
    padding: "14px 0",
    borderBottom: `1px solid ${BORDER}`,
    fontSize: "13px",
    color: TEXT,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  headRow: {
    fontSize: "12px",
    fontWeight: 500,
    color: MUTED,
    padding: "12px 0",
  },
  value: {
    wordBreak: "break-word",
  },
  message: {
    padding: "30px 0",
    textAlign: "center",
    fontSize: "14px",
    color: MUTED,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
  },
}));

type StopFactorsModalProps = {
  owner: StopFactorOwner | null;
  open: boolean;
  onClose: () => void;
  /** Вызывается после успешного пересчёта — чтобы обновить индикатор в списке. */
  onRecalculated?: (report: StopFactorReport) => void;
};

const DOT_COLORS: Record<StopFactorStatus, string> = {
  Yes: "red",
  No: "green",
  NoData: "gray",
  NotApplicable: "gray",
};

const RESULT_COLORS: Record<StopFactorStatus, string> = {
  Yes: RED,
  No: GREEN,
  NoData: MUTED,
  NotApplicable: MUTED,
};

const StopFactorsModal = ({ owner, open, onClose, onRecalculated }: StopFactorsModalProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [report, setReport] = useState<StopFactorReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  // Гость видит результаты, но пересчитывать не может — так же ограничено на бэке.
  const canRecalculate = getRoleSafe() !== "CustomerGuest";

  const ownerType = owner?.type;
  const ownerId = owner?.id;
  const ownerCustomerId = owner?.customerId;

  useEffect(() => {
    setReport(null);
    if (!open || !ownerType || !ownerId) return;

    let cancelled = false;
    setLoading(true);
    getStopFactorReport({ type: ownerType, id: ownerId, customerId: ownerCustomerId })
      .then((data) => {
        if (!cancelled) setReport(data);
      })
      .catch((e) => console.error("[getStopFactorReport]", e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, ownerType, ownerId, ownerCustomerId]);

  const handleRecalculate = () => {
    if (!ownerType || !ownerId) return;
    setRecalculating(true);
    recalculateStopFactors({ type: ownerType, id: ownerId, customerId: ownerCustomerId })
      .then((data) => {
        setReport(data);
        onRecalculated?.(data);
      })
      .catch((e) => console.error("[recalculateStopFactors]", e))
      .finally(() => setRecalculating(false));
  };

  if (!owner) return null;

  const items = report?.items ?? [];
  const triggeredCount = report?.triggeredCount ?? 0;

  const noDataCount = items.filter((i) => i.status === "NoData").length;

  const renderResult = (status: StopFactorStatus | null) => {
    switch (status) {
      case "Yes":
        return t("StopFactors.yes");
      case "No":
        return t("StopFactors.no");
      case "NoData":
        return t("StopFactors.noData");
      case "NotApplicable":
        return <span title={t("StopFactors.notApplicable")}>–</span>;
      default:
        return "–";
    }
  };

  const renderRow = (item: StopFactorResult) => (
    <div key={item.key} className={classes.row}>
      <FiberManualRecordIcon
        style={{
          color: getPointColorFromString(item.status ? DOT_COLORS[item.status] : "gray"),
          fontSize: 18,
        }}
      />
      <div>{item.displayName}</div>
      <div className={classes.value}>{item.value}</div>
      <div style={{ fontWeight: 600, color: item.status ? RESULT_COLORS[item.status] : MUTED }}>
        {renderResult(item.status)}
      </div>
    </div>
  );

  const renderSummary = () =>
    report?.calculatedAt ? (
      <div className={classes.summary}>
        <span>
          {t("StopFactors.checked")}: <b>{items.length}</b>
          {" · "}
          {t("StopFactors.triggered")}:{" "}
          <b style={{ color: triggeredCount > 0 ? RED : GREEN }}>{triggeredCount}</b>
          {noDataCount > 0 && (
            <>
              {" · "}
              {t("StopFactors.noData")}: <b>{noDataCount}</b>
            </>
          )}
          {" · "}
          {t("StopFactors.source")}: <b>{t("StopFactors.sourceName")}</b>
        </span>
        <span>
          {t("StopFactors.calculatedAt")}: {report.calculatedAt.toLocaleString("ru-RU")}
        </span>
      </div>
    ) : (
      <div className={classes.summary}>{t("StopFactors.notCalculated")}</div>
    );

  const renderContent = () => {
    if (loading) {
      return (
        <div className={classes.message}>
          <CircularProgress size={28} />
        </div>
      );
    }

    if (items.length === 0) {
      return <div className={classes.message}>{t("StopFactors.noEnabled")}</div>;
    }

    return (
      <>
        {renderSummary()}
        <div className={`${classes.row} ${classes.headRow}`}>
          <span />
          <span>{t("StopFactors.criterion")}</span>
          <span>{t("StopFactors.value")}</span>
          <span>{t("StopFactors.result")}</span>
        </div>
        {items.map(renderRow)}
      </>
    );
  };

  const footer = canRecalculate ? (
    <div className={classes.footer}>
      <ButtonBB
        onClick={handleRecalculate}
        disabled={loading || recalculating || items.length === 0}
        startIcon={
          recalculating ? <CircularProgress size={16} /> : <AutorenewIcon style={{ fontSize: 18 }} />
        }
      >
        {t("buttons.Recalculate")}
      </ButtonBB>
    </div>
  ) : null;

  return (
    <ModalLayout
      open={open}
      onClose={onClose}
      title="StopFactors.title"
      subtitle={[owner.name, owner.inn].filter(Boolean).join(" · ")}
      maxWidth="880px"
      footer={footer}
    >
      {renderContent()}
    </ModalLayout>
  );
};

export default StopFactorsModal;
